
import React, { Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, Outlet } from 'react-router-dom';
import Header from './components/common/Header';
import Footer from './components/common/Footer';
import HomePage from './pages/HomePage';
import ProtectedRoute from './components/auth/ProtectedRoute';
import AdminRoute from './components/auth/AdminRoute';
import { useProducts } from './hooks/useProducts';
import { slugify } from './utils/slugify';
import GeneralDiscountBanner from './components/common/GeneralDiscountBanner';
import GoogleTagInjector from './components/common/GoogleTagInjector';
import BrandThemeInjector from './components/common/BrandThemeInjector';

// Lazy-loaded pages (code-split por ruta)
const AdminPage = React.lazy(() => import('./pages/AdminPage'));
const AdminProductEditPage = React.lazy(() => import('./pages/AdminProductEditPage'));
const AdminProductCreatePage = React.lazy(() => import('./pages/AdminProductCreatePage'));
const AdminSolicitReviewPage = React.lazy(() => import('./pages/AdminSolicitReviewPage'));
const OrderTrackingPage = React.lazy(() => import('./pages/OrderTrackingPage'));
const ProductDetailPage = React.lazy(() => import('./pages/ProductDetailPage'));
const DesignPage = React.lazy(() => import('./pages/DesignPage'));
const RequestReviewPage = React.lazy(() => import('./pages/RequestReviewPage'));
const RequestReplicaPage = React.lazy(() => import('./pages/RequestReplicaPage'));
const RequestCustomDesignPage = React.lazy(() => import('./pages/RequestCustomDesignPage'));
const FreeDesignPage = React.lazy(() => import('./pages/FreeDesignPage'));
const LoginPage = React.lazy(() => import('./pages/LoginPage'));
const CartPage = React.lazy(() => import('./pages/CartPage'));
const CheckoutAuthPage = React.lazy(() => import('./pages/CheckoutAuthPage'));
const ShippingPage = React.lazy(() => import('./pages/ShippingPage'));
const PaymentPage = React.lazy(() => import('./pages/PaymentPage'));
const SearchResultsPage = React.lazy(() => import('./pages/SearchResultsPage'));
const PaymentSuccessPage = React.lazy(() => import('./pages/PaymentSuccessPage'));
const PaymentCancelledPage = React.lazy(() => import('./pages/PaymentCancelledPage'));
const ClientOrdersPage = React.lazy(() => import('./pages/ClientOrdersPage'));
const PrivacyPolicyPage = React.lazy(() => import('./pages/PrivacyPolicyPage'));
const DataDeletionPage = React.lazy(() => import('./pages/DataDeletionPage'));
const ReturnPolicyPage = React.lazy(() => import('./pages/ReturnPolicyPage'));
const ContentEngineTermsPage = React.lazy(() => import('./pages/ContentEngineTermsPage'));
const ContentEnginePrivacyPage = React.lazy(() => import('./pages/ContentEnginePrivacyPage'));

const PageSpinner = () => (
    <div className="flex justify-center items-center h-64">
        <i className="fas fa-spinner fa-spin text-3xl text-blue-600"></i>
    </div>
);

/** Skeleton que replica la estructura del hero + productos de HomePage */
const HomePageSkeleton = () => (
    <div>
        {/* Hero skeleton */}
        <section className="bg-slate-950 text-white">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 lg:py-32">
                <div className="max-w-2xl space-y-6">
                    <h1 className="text-5xl md:text-7xl font-extrabold tracking-tight leading-[1.1]">
                        Impresiones <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-300">Premium</span> a la Velocidad de tu Negocio.
                    </h1>
                    <p className="text-lg md:text-xl text-slate-400 leading-relaxed">
                        No esperes días por lo que necesitas hoy. Imprime tus ideas con tecnología de punta y recíbelas en horas.
                    </p>
                    <div className="flex gap-5 pt-4">
                        <span className="bg-blue-600 text-white font-bold py-4 px-10 rounded-xl">Ver Catálogo</span>
                        <span className="bg-white/5 border border-white/10 text-white font-bold py-4 px-10 rounded-xl">Rastrear Pedido</span>
                    </div>
                </div>
            </div>
        </section>
        {/* Product grid skeleton */}
        <div className="bg-white pt-24 pb-16 rounded-t-[3rem] -mt-10 relative z-20">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                <div className="text-center mb-12">
                    <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900">Nuestros Productos Estrella</h2>
                    <p className="text-gray-500 mt-3 text-lg">Soluciones de impresión profesionales para elevar la imagen de tu marca.</p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 animate-pulse">
                    {[1,2,3,4,5,6].map(i => (
                        <div key={i} className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                            <div className="aspect-[4/3] bg-gray-200" />
                            <div className="p-5 space-y-3">
                                <div className="h-4 bg-gray-200 rounded w-3/4" />
                                <div className="h-3 bg-gray-200 rounded w-1/2" />
                                <div className="h-8 bg-gray-200 rounded w-1/3 mt-4" />
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    </div>
);

/** Skeleton que replica la estructura de la página de producto */
const ProductDetailSkeleton = () => (
    <div className="bg-white min-h-screen pb-24">
        <div className="flex flex-col lg:flex-row items-start">
            {/* Left: Image area */}
            <div className="lg:w-[55%] w-full bg-gray-50 flex items-center justify-center h-[25vh] lg:h-[calc(100vh-64px)] border-b lg:border-b-0 lg:border-r border-gray-100">
                <div className="w-3/4 h-3/4 bg-gray-200 rounded-xl animate-pulse" />
            </div>
            {/* Right: Content */}
            <div className="lg:w-[45%] w-full p-6 lg:p-10 space-y-6 animate-pulse">
                <div className="h-3 bg-gray-200 rounded w-1/3" />
                <div className="h-8 bg-gray-200 rounded w-3/4" />
                <div className="h-4 bg-gray-200 rounded w-full" />
                <div className="h-4 bg-gray-200 rounded w-5/6" />
                <div className="mt-6 space-y-3">
                    <div className="h-10 bg-gray-200 rounded-xl w-full" />
                    <div className="h-10 bg-gray-200 rounded-xl w-full" />
                    <div className="h-10 bg-gray-200 rounded-xl w-full" />
                </div>
                <div className="mt-6 flex gap-4">
                    <div className="h-12 bg-gray-200 rounded-xl w-1/2" />
                    <div className="h-12 bg-blue-100 rounded-xl w-1/2" />
                </div>
            </div>
        </div>
    </div>
);

const AppLayout: React.FC = () => {
    const location = useLocation();
    const isLoginPage = location.pathname === '/login';
    const isAdminPage = location.pathname.startsWith('/admin');
    const isCheckoutPage = location.pathname.startsWith('/checkout');

    const { isLoading, products } = useProducts();

    const pathParts = location.pathname.split('/').filter(Boolean);
    let isProductOrDesignPage = false;

    if (!isLoading && (pathParts.length === 2 || (pathParts.length >= 3 && pathParts[2] === 'design'))) {
        const [categorySlug, productSlug] = pathParts;
        const product = products.find(p => {
            return slugify(p.category) === categorySlug && p.seo?.slug === productSlug;
        });
        if (product) {
            isProductOrDesignPage = true;
        }
    }

    const hideFooter = location.pathname.startsWith('/solicitar-resena') || isAdminPage || isLoginPage || isProductOrDesignPage || location.pathname === '/carrito' || isCheckoutPage || location.pathname.startsWith('/track');

    // Determinar qué skeleton mostrar según la ruta
    const getLoadingSkeleton = () => {
        if (location.pathname === '/') return <HomePageSkeleton />;
        if (pathParts.length === 2) return <ProductDetailSkeleton />;
        return <PageSpinner />;
    };

    return (
        <div className="flex flex-col min-h-screen">
            <GoogleTagInjector />
            <BrandThemeInjector />
            {!isLoginPage && <Header />}
            <GeneralDiscountBanner />
            <main className={`flex-grow ${!isAdminPage && !isLoginPage ? '' : ''}`}>
                {isLoading ? (
                    getLoadingSkeleton()
                ) : (
                    <Suspense fallback={<PageSpinner />}>
                        <Outlet />
                    </Suspense>
                )}
            </main>
            {!hideFooter && <Footer />}
        </div>
    );
}

const App: React.FC = () => {
    return (
        <BrowserRouter>
            <Routes>
                <Route element={<AppLayout />}>
                    <Route path="/" element={<HomePage />} />
                    <Route path="/search" element={<SearchResultsPage />} />
                    <Route path="/:categorySlug/:productSlug" element={<ProductDetailPage />} />
                    <Route path="/:categorySlug/:productSlug/design" element={<DesignPage />} />
                    <Route path="/:categorySlug/:productSlug/design/custom" element={<RequestCustomDesignPage />} />
                    <Route path="/:categorySlug/:productSlug/design/replica" element={<RequestReplicaPage />} />
                    <Route path="/:categorySlug/:productSlug/design/free" element={<FreeDesignPage />} />
                    <Route path="/track" element={<OrderTrackingPage />} />
                    <Route path="/track/:orderId" element={<OrderTrackingPage />} />
                    <Route path="/solicitar-resena/:reviewId" element={<RequestReviewPage />} />
                    <Route path="/aviso-de-privacidad" element={<PrivacyPolicyPage />} />
                    <Route path="/eliminacion-de-datos" element={<DataDeletionPage />} />
                    <Route path="/politica-de-devoluciones" element={<ReturnPolicyPage />} />
                    <Route path="/contentengine/terminos-de-servicio" element={<ContentEngineTermsPage />} />
                    <Route path="/contentengine/aviso-de-privacidad" element={<ContentEnginePrivacyPage />} />
                    <Route path="/login" element={<LoginPage />} />
                    <Route path="/carrito" element={<CartPage />} />
                    <Route path="/checkout/auth" element={<CheckoutAuthPage />} />
                    <Route path="/checkout/success" element={<PaymentSuccessPage />} />
                    <Route path="/checkout/cancelled" element={<PaymentCancelledPage />} />


                    <Route element={<ProtectedRoute />}>
                        <Route path="/checkout/shipping" element={<ShippingPage />} />
                        <Route path="/checkout/payment" element={<PaymentPage />} />
                        <Route path="/mis-pedidos" element={<ClientOrdersPage />} />
                    </Route>
                    
                    <Route element={<AdminRoute />}>
                        <Route path="/admin" element={<AdminPage />} />
                        <Route path="/admin/product/edit/:productId" element={<AdminProductEditPage />} />
                        <Route path="/admin/product/new" element={<AdminProductCreatePage />} />
                        <Route path="/admin/solicit-review/:reviewId" element={<AdminSolicitReviewPage />} />
                    </Route>

                    <Route path="*" element={<Navigate to="/" />} />
                </Route>
            </Routes>
        </BrowserRouter>
    );
};

export default App;
